import {
  MODULES,
  billingLabel,
  isEmailAllowed,
  money,
  seatConsumption,
  seatsUsed,
  supportByPlan,
  validateCorporateDomain,
  type AccessReviewStatus,
  type ApprovalRequest,
  type AuditEntry,
  type Group,
  type ImpersonationSession,
  type Role,
  type Tenant,
  type TenantUser,
} from '@titan/shared';
import { SHIPPED_MODULES } from '@titan/modules';
import { Eye, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useSession } from '../../session/SessionProvider.js';
import { Loading, ScreenBody, ScreenHeader } from '../../shell/Shell.js';
import {
  Button,
  Card,
  ConfirmButton,
  DefinitionList,
  InlineError,
  InlineInfo,
  KpiCard,
  KpiStrip,
  Pill,
  PillRow,
  ProgressBar,
  TextInput,
  Tile,
  Tiles,
  ToggleRow,
  TwoColumn,
  planTone,
  tenantStatusTone,
} from '../../ui/index.js';
import { AccessCenter } from '../access/AccessCenter.js';
import { useApprovalNotice } from '../access/useNotice.js';
import { errorMessage, useLoad } from '../useLoad.js';
import { SsoCard } from './SsoCard.js';
import styles from './Admin.module.css';

interface TenantDetailResponse {
  tenant: Tenant;
  users: TenantUser[];
  groups: Group[];
  roles: Role[];
  audit: AuditEntry[];
  approvals: ApprovalRequest[];
  accessReview: AccessReviewStatus;
  impersonation: { ok: true; persona: { name: string; email: string } } | { ok: false; reason: string; message: string };
}

/** Spec 07 Tenant detail: contract, entitlements, domains and SSO above; the access center below. */
export function TenantDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { api, me, startImpersonation } = useSession();
  const { data, error, loading, reload } = useLoad<TenantDetailResponse>(`/admin/tenants/${id}`);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [domainInput, setDomainInput] = useState('');
  const { notice, check, clear } = useApprovalNotice();

  if (loading && !data) return <Loading />;
  if (error && !data) {
    return (
      <ScreenBody>
        <InlineError>{error}</InlineError>
      </ScreenBody>
    );
  }
  if (!data || !me) return null;
  const { tenant, users, groups, roles, audit, approvals, accessReview, impersonation } = data;
  const suspended = tenant.status === 'Suspended';
  const activeUsers = users.filter((u) => u.status === 'Active').length;

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    clear();
    try {
      check(await work());
      await reload();
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const patch = (body: Record<string, unknown>) => run(() => api(`/admin/tenants/${tenant.id}`, { method: 'PATCH', body: JSON.stringify(body) }));

  const impersonate = () =>
    run(async () => {
      const res = await api<{ token: string; session: ImpersonationSession }>(`/admin/tenants/${tenant.id}/impersonate`, { method: 'POST' });
      await startImpersonation(res.token, res.session);
      navigate('/');
    });

  const setDomains = (domains: string[]) => run(() => api(`/admin/tenants/${tenant.id}/domains`, { method: 'PUT', body: JSON.stringify({ domains }) }));
  const addDomain = () => {
    const v = validateCorporateDomain(domainInput);
    if (!v.ok) {
      setActionError(v.message);
      return;
    }
    setDomainInput('');
    if (!tenant.allowedDomains.includes(v.domain)) void setDomains([...tenant.allowedDomains, v.domain]);
  };
  const strandedBy = (domain: string) => users.filter((u) => !isEmailAllowed(u.email, tenant.allowedDomains.filter((d) => d !== domain)));

  return (
    <>
      <ScreenHeader
        detail
        eyebrow="administration · tenant"
        back={{ label: 'Tenants', to: '/admin/tenants' }}
        title={tenant.name}
        titleAside={
          <PillRow>
            <Pill tone={tenantStatusTone(tenant.status)}>{tenant.status}</Pill>
            <Pill tone={planTone(tenant.plan)} variant="tier">
              {tenant.plan}
            </Pill>
          </PillRow>
        }
        meta={
          <div className={styles.metaLine}>
            {tenant.industry} · client since {tenant.since} · CSM {tenant.csm}
          </div>
        }
        actions={
          <div className={styles.actionsCol}>
            <Button icon={<Eye size={15} strokeWidth={1.75} aria-hidden="true" />} disabled={!impersonation.ok || busy} onClick={impersonate}>
              Impersonate this tenant
            </Button>
            {!impersonation.ok && <span className={styles.reason}>{impersonation.message}</span>}
            <Button variant="secondary" onClick={() => navigate(`/organization?tenantId=${encodeURIComponent(tenant.id)}`)}>
              Their user management
            </Button>
            <ConfirmButton
              label={suspended ? 'Reactivate tenant' : 'Suspend tenant'}
              variant="destructive"
              size="md"
              tone={suspended ? 'warning' : 'critical'}
              disabled={busy}
              message={
                suspended
                  ? `Access returns for ${users.length} ${users.length === 1 ? 'person' : 'people'} and the monthly contract of ${money(tenant.mrrBeforeSuspension ?? 0)} is restored.`
                  : `${activeUsers} active ${activeUsers === 1 ? 'person loses' : 'people lose'} access immediately, every session ends and the superadmins are told. Billing pauses. Nothing is deleted. A second Platform Owner has to approve.`
              }
              confirmLabel={suspended ? 'Reactivate' : 'Ask for approval'}
              onConfirm={() => patch({ status: suspended ? 'Active' : 'Suspended' })}
            />
          </div>
        }
      />
      <ScreenBody>
        {actionError && <InlineError>{actionError}</InlineError>}
        {notice && <InlineInfo>{notice}</InlineInfo>}
        <KpiStrip min={190}>
          <KpiCard label="Seats used" figure={`${seatsUsed(users)} of ${tenant.seats}`}>
            <ProgressBar value={seatConsumption(tenant, users)} warnAbove={0.85} aria-label="Seat consumption" />
          </KpiCard>
          <KpiCard label="Monthly contract" figure={billingLabel(tenant, money)} note={supportByPlan(tenant.plan)} />
          <KpiCard label="Single sign-on" figure={tenant.ssoProvider?.label ?? 'Not configured'} note={tenant.ssoProvider ? (tenant.ssoProvider.enforce ? 'Required for everyone' : 'Optional, passwords allowed') : tenant.mfa} />
          <KpiCard
            label="Access review"
            figure={accessReview.lastCompletedAt ? new Date(accessReview.lastCompletedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Never'}
            tone={accessReview.overdue ? 'warning' : 'default'}
            note={`${accessReview.overdue ? 'Overdue since' : 'Next due'} ${new Date(accessReview.dueAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
          />
        </KpiStrip>

        <TwoColumn>
          <Card title="Entitlements" description="What this tenant can open in the hub. Only DinoCloud changes these. Modules appear here as each one ships.">
            {SHIPPED_MODULES.length === 0 && <span className={styles.reason}>No product module has shipped yet. The tenant has its user management; entitlements arrive with the first module.</span>}
            <div className={styles.toggleList}>
              {SHIPPED_MODULES.map((k) => (
                <ToggleRow key={k} label={MODULES[k].label} note={MODULES[k].description} checked={tenant.modules[k]} disabled={busy} onChange={(on) => patch({ modules: { [k]: on } })} />
              ))}
            </div>
          </Card>
          <Card title="Allowed email domains" description="People at this tenant sign in and are invited only with addresses on these domains. Corporate domains only. Changes wait for a second Platform Owner.">
            <div className={styles.chipsEdit}>
              {tenant.allowedDomains.map((d) => {
                const stranded = strandedBy(d);
                const strandsSuperadmin = stranded.some((u) => u.isSuperadmin);
                return (
                  <span key={d} className={styles.domainChip}>
                    {d}
                    {tenant.allowedDomains.length > 1 && !strandsSuperadmin && (
                      <ConfirmButton
                        label={<X size={12} strokeWidth={1.75} aria-label={`Remove ${d}`} />}
                        variant="link"
                        disabled={busy}
                        message={stranded.length ? `${stranded.length} ${stranded.length === 1 ? 'person' : 'people'} on ${d} could no longer sign in: ${stranded.map((u) => u.email).join(', ')}.` : `Nobody uses ${d} today. Removing it changes nobody’s access.`}
                        confirmLabel="Ask for approval"
                        onConfirm={() => setDomains(tenant.allowedDomains.filter((x) => x !== d))}
                      />
                    )}
                  </span>
                );
              })}
            </div>
            <div className={styles.inline}>
              <TextInput
                mono
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addDomain();
                  }
                }}
                placeholder="company.com"
                aria-label="Domain to allow"
              />
              <Button variant="secondary" onClick={addDomain} disabled={busy || !domainInput.trim()}>
                Add domain
              </Button>
            </div>
          </Card>
        </TwoColumn>

        <TwoColumn>
          <SsoCard tenant={tenant} busy={busy} run={run} />
          <Card title="Linked AWS accounts" description="Accessed through the DinoCloud cross-account role.">
            <Tiles>
              {tenant.accounts.length === 0 && <Tile empty>No account linked yet. The tenant deploys the cross-account role, then it appears here.</Tile>}
              {tenant.accounts.map((a) => (
                <Tile key={a}>{a}</Tile>
              ))}
            </Tiles>
            <DefinitionList
              items={[
                { label: 'Multi-factor', value: tenant.ssoProvider?.enforce ? 'At the identity provider' : tenant.mfa },
                { label: 'Data residency', value: tenant.region },
                { label: 'Session length', value: '8 hours, re-auth for admin actions' },
                { label: 'Support', value: supportByPlan(tenant.plan) },
              ]}
            />
          </Card>
        </TwoColumn>

        <AccessCenter
          tenant={{ id: tenant.id, name: tenant.name, seats: tenant.seats, allowedDomains: tenant.allowedDomains, modules: tenant.modules, ssoLabel: tenant.ssoProvider?.label ?? null }}
          users={users}
          groups={groups}
          roles={roles}
          audit={audit}
          approvals={approvals}
          accessReview={accessReview}
          actor={me.actor}
          staffTenantId={tenant.id}
          canWrite
          onChanged={reload}
        />
      </ScreenBody>
    </>
  );
}
